import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleInitGame, handleInitFromReplayLink, handleGetBalanceStrategy, handleGetBalance } from "../../service";
import { symbolData } from "../../SymbolData";
import { loadAssets } from "./assetsSlice";
import { loadSounds, resetSoundSettings } from "./soundsSlice";
import { rgsPlay, rgsBuyFeature } from "./spinSlice";
import StorageManager from "../../utils/StorageManager";
import {
  COMMAND_KIND,
  kindOfCommand,
  kindOfSpinArg,
} from "../../utils/spinCommands";
import {
  DEV_SIMULATION_ENABLED,
  DEV_SIMULATION_MODE,
  DEV_SIMULATION_ROUND,
  DEV_SCATTER_TRIGGER_ROUND,
  DEV_SCATTER_ANTICIPATION_ROUND,
  DEV_COIN_ANTICIPATION_ROUND,
  DEV_COIN_TRAILS_ROUND,
  DEV_JACKPOT_COINS_ROUND,
  DEV_HP_ROWS_ROUND,
  DEV_WIN_TRIGGER_ROUND,
  DEV_WIN_TRIGGER_MULTIPLIER,
  DEV_MYSTERY_ROUND,
  DEV_GOLDEN_MYSTERY_ROUND,
  DEV_GOLDEN_QUIET_ROUND,
  DEV_GOLDEN_FULL_ROUND,
  DEV_GOLDEN_MAX_ROUND,
  DEV_GOLDEN_CHAIN_ROUND,
  DEV_GOLDEN_BIG_ROUND,
  DEV_GOLDEN_GAMBLE_ROUND,
  DEV_GOLDEN_GAMBLE_TWO_ROUND,
  DEV_GOLDEN_BLANK_ROUND,
  DEV_GOLDEN_REROLL_ROUND,
  DEV_GOLDEN_REROLL_AWARD_ROUND,
  DEV_SCATTER_BLANK_ROUND,
  DEV_GOLDEN_AWARD_ROUND,
  DEV_GOLDEN_ALL_ROUND,
  DEV_GOLDEN_MULT_ROUND,
  DEV_MYSTERY_SHORT_ROUND,
  DEV_WIN_MULTIPLIER,
} from "../../devSimulation";
import { FEATURES_CONFIG } from "../../featuresConfig";
import {
  openInfoPopup,
  closeIntroPopup,
  setUserInterfaceDisabled,
} from "./userInterfaceSlice";
import {
  setEnterRound,
  setEndedRound,
  startReplaySession,
  endReplaySession,
  fetchReplayInit,
} from "./replaySlice";

import { resetUISettings } from "./settingsSlice";

import { isStandaloneReplayMode, getStandaloneReplayUrl } from "../../utils/launcherOptions";
import { soundManager } from "../../SoundManager";
import { withTimeout } from "../../utils/asyncTimeout";

// Longest the boot waits for the RGS to answer before it gives up.
//
// A request that FAILS was always handled — it throws, this thunk rejects, and
// LoadingErrorPopup takes the screen with a Reload button. A request that is
// accepted and then never answered was not: the thunk never settled, status
// stayed "loading" forever, and the player sat on the intro popup watching the
// wordmark loop with no error and nothing to press. This is the only failure
// on the boot path that has to be FATAL — there is no game without a session.
//
// Deliberately generous. This is the one timeout that can turn a player away,
// so it is set far beyond any honest round trip — a slow connection must never
// be mistaken for a dead one. It also covers getRGS(), which pulls the provider
// module in, so a CDN that hangs is caught by the same deadline.
const INIT_REQUEST_TIMEOUT_MS = 30000;

// Sounds are not worth stopping a boot for, so this one only stops the WAIT.
// Without it a stalled sound download pins status on "loading" and the player
// never gets a tap-to-start, which is a far worse outcome than a silent game.
const SOUNDS_TIMEOUT_MS = 30000;

// Everything a replay spin overwrites in this slice (see rgsPlay.fulfilled).
// Snapshotted when a replay session starts, restored when it ends, so the live
// game comes back exactly as the player left it. bet/balance/displayedBalance
// are absent on purpose — replay never writes those (RGS returns them as
// undefined), so there is nothing to protect.
const REPLAY_SNAPSHOT_FIELDS = [
  "currentRound",
  "gameMode",
  "featureState",
  "features",
  "availableActions",
  "flowState",
  "freeRounds",
  "potProgress",
  "lastCommand",
];

// Async thunk for initializing the game and fetching initial data
export const initializeGame = createAsyncThunk(
  "gameSettings/initializeGame",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // Standalone replay link (docs/replay-history-spec.md §0, Stage 9) —
      // this page was opened via a shared "Copy link" URL, not the normal
      // game entry. Confirmed live (2026-08-10): window.__OPTIONS__.api and
      // .play_token are both empty in this mode, so handleInitGame()'s own
      // missing-fields check would throw immediately if called — there is
      // no real session to validate/restore at all, so skip straight to
      // the replay boot instead of STEP 1/2 below.
      if (isStandaloneReplayMode()) {
        const replayUrl = getStandaloneReplayUrl();
        const result = await withTimeout(
          handleInitFromReplayLink(replayUrl),
          INIT_REQUEST_TIMEOUT_MS,
          "replay link init",
        );

        // Same as the live path below — non-fatal, asset/sound loading is
        // unaffected by which branch got us here.
        try {
          await dispatch(loadAssets()).unwrap();
        } catch (assetError) {
          console.warn("[initializeGame] loadAssets failed, continuing:", assetError);
        }
        try {
          await withTimeout(
            dispatch(loadSounds()).unwrap(),
            SOUNDS_TIMEOUT_MS,
            "sound bundle",
          );
        } catch (soundError) {
          console.warn("[initializeGame] loadSounds failed, continuing:", soundError);
        }

        // No Intro ceremony for a standalone replay page — straight from
        // loader to the Enter modal (confirmed §0: skipping it costs no
        // load time, assets above already loaded in full either way).
        // sessionActive flips on immediately (not waiting for a Play click
        // like the in-game hot-swap path) so every §9 restriction — disabled
        // buttons, hidden History — applies for this page's entire
        // lifetime, not just once a round starts. `standalone: true` keeps
        // the balance-protection machinery (built for protecting a LIVE
        // wallet from replay data) out of the way, since there's no live
        // wallet here at all — state.balance below already legitimately
        // IS the replay balance. enterRound omits replayUrl on purpose:
        // the Enter modal's Play button only re-fetches when that field is
        // present, and here we're already fully booted into replay mode.
        // endedRound is pre-staged the same way handleEnterPlay does for
        // the in-game path — the Ended modal needs it the moment the round
        // finishes, and nothing else will ever set it in standalone mode.
        // `userInterfaceDisabled` starts true and is normally cleared by the
        // intro popup once it appears — but this mode has no intro popup at
        // all (it isn't even mounted, see Stage.js), so clear it here or the
        // Spin button stays permanently blocked with nothing left to unblock
        // it. Replay's own restrictions are enforced separately via
        // selectReplaySessionActive on each button.
        dispatch(setUserInterfaceDisabled(false));
        dispatch(closeIntroPopup());
        // ── The intro's SOUND gate has to be released here too ──
        //
        // SoundManager mutes every sound but the intro's own while
        // `musicPlaybackDeferred` is true, and the ONE thing that clears it is
        // IntroPopupComposed's close handler — a popup this mode never mounts
        // (see the note above). So a standalone replay booted straight into a
        // silent game: reels, wins, popups, all muted, for the whole session.
        // Found in the 2026-09-23 review of the gate; before it this page had
        // its SFX. Same call the intro popup makes, at the same moment — the
        // screen is now the player's.
        soundManager.releaseMusicPlayback();
        dispatch(startReplaySession({ standalone: true }));
        dispatch(setEnterRound({ bet: result.bet, time: null }));
        dispatch(setEndedRound({ bet: result.bet, time: null }));
        // Seed replaySlice's own bet/balance/step-cursor state the same way
        // the in-game path does. initFromReplayLink above already primed the
        // RGS object, but replaySlice drives the DISPLAY side — its
        // replayBalance is what BalanceDisplay shows and what applyReplayWin
        // adds each step's win onto. Without this the first playthrough has
        // no pre-win starting balance to reveal from, and every "Replay
        // Again" would resume from wherever the last one ended instead of
        // resetting. Same call "Replay Again" makes, so both take one code
        // path. Not awaited — the displays fall back to state.game until it
        // lands, and the RGS cursor it resets was already at zero.
        dispatch(fetchReplayInit());
        // NOTE: the Enter modal is deliberately NOT opened here. ReplayOverlay
        // is HTML mounted in App.js — outside the PIXI canvas — so it paints
        // OVER the loader, not under it. Opening it now (the loader still has
        // ~1s of settle + fade left) shows the popup floating on the loader's
        // black background, then the game appearing behind it. ReplayOverlay
        // opens it itself once `loaderFadedOut` is true, so the order is
        // loader → game → popup.

        return { result };
      }

      // STEP 1: Check session validity FIRST
      // Uses cache_player_id from window.__OPTIONS__ to detect player changes.
      // On their production server, the wrapper page sets cache_player_id before
      // loading the game. If it differs from what we stored, a new player is
      // using this browser — clear all stale localStorage data.
      const currentSessionToken = StorageManager.getCurrentSessionToken();
      const isValidSession = StorageManager.isSessionValid();

      if (!isValidSession) {
        // New session detected — clear all session-specific data
        StorageManager.clearSessionData();

        // Reset in-memory Redux state to defaults (slices loaded from
        // localStorage at module init, before clearSessionData runs)
        dispatch(resetSoundSettings());
        dispatch(resetUISettings());

        // Save new session token (cache_player_id)
        if (currentSessionToken) {
          StorageManager.saveSessionToken(currentSessionToken);
        }
      }

      // STEP 2: API call — validate session before downloading heavy assets
      // If this fails (expired session, server down, invalid token),
      // the user sees an error immediately without wasting bandwidth
      const result = await withTimeout(
        handleInitGame(),
        INIT_REQUEST_TIMEOUT_MS,
        "init request",
      );

      // Session is valid — now load assets and sounds (non-fatal; loader error is init-only)
      try {
        await dispatch(loadAssets()).unwrap();
      } catch (assetError) {
        console.warn("[initializeGame] loadAssets failed, continuing:", assetError);
      }
      try {
        await withTimeout(
          dispatch(loadSounds()).unwrap(),
          SOUNDS_TIMEOUT_MS,
          "sound bundle",
        );
      } catch {
        // Sounds are not worth stopping a boot for, and this fires on every
        // dev load, so it is not worth a console line either. Silent by
        // choice — loadAssets above still speaks, because a missing asset is
        // a real problem.
      }

      // STEP 3: If free rounds are active, open InfoPopup immediately.
      // It will sit behind the IntroPopup and become visible when intro closes.
      // Skip when remaining === 0 (last round triggered bonus — popup is pointless)
      if (FEATURES_CONFIG.FREE_ROUNDS.ENABLED && result.freeRounds?.status === "active") {
        const performed = result.freeRounds.performed;
        const count = result.freeRounds.count;
        const remaining = count - performed;

        if (remaining > 0) {
          dispatch(openInfoPopup(
            performed === 0
              ? { key: "you_have_been_credited_with_free_rounds", data: { N: count } }
              : { key: "you_have_free_rounds_left", data: { N: remaining } }
          ));
        }
      }

      // Data is loaded - feature slices will react to universal state
      return { result };
    } catch (error) {
      // Nothing downstream of this thunk rejecting shows the real message
      // (loadingProgressSlice just flips to the generic "Something went
      // wrong" screen) — log it so it's actually visible in devtools when
      // debugging a boot failure, standalone replay or otherwise.
      console.error("[initializeGame] failed:", error);
      // Structured errors (e.g. synthesized 206 from FR cancellation) — pass through for error popup
      if (error?.errors && Array.isArray(error.errors)) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error.message);
    }
  },
);

/**
 * ── No live request may leave the page while a replay is on screen ──
 *
 * Every thunk below calls `handleInitGame()`, which is not a read: PopiPlay's
 * initGame() POSTs the live `init` command AND rewrites the player's real
 * round-recovery localStorage (saveBaseGameRound / clearBonusGameRound /
 * clearFreeRoundsRound and the rest). Firing it while a RECORDED round owns
 * the screen throws the live session's recovery records away using replay
 * data as the reason — and a player who then reloads is recovered into the
 * wrong round, which is how a client ends up sending a command the server has
 * already closed. Two of those is a ban (docs/api-contract-mad-labs-freespins.md §0).
 *
 * It was reachable. TransitionDialog's free-spins outro syncs the balance
 * unconditionally, and a failed replay-step fetch drops into the live error
 * recovery in WarningPopup / ErrorRecoveryOverlay — both of which run inside a
 * replay exactly as they run live.
 *
 * A `condition` rather than a check inside each body, for the same reason
 * rgsPlay carries one: it refuses before the thunk runs, so no request is
 * built at all, and there is one place to read instead of five call sites to
 * keep in step.
 *
 * `initializeGame` deliberately has NO such guard — it is the boot and the
 * release valve, and it is what the standalone replay link boots through.
 */
const notWhileReplaying = (_arg, { getState }) =>
  !getState().replay?.sessionActive;

/**
 * Heartbeat sync - calls init API to sync balance
 * Does NOT reload assets/sounds, only updates balance
 * CRITICAL: Only extracts balance to avoid unnecessary component re-renders
 * Note: Backend treats this as session keep-alive, but frontend only cares about balance
 */
export const heartbeatSync = createAsyncThunk(
  "gameSettings/heartbeatSync",
  async (_, { rejectWithValue }) => {
    try {
      const result = await handleInitGame();
      // Return only balance to avoid triggering unnecessary updates
      return { balance: result.balance };
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error.message);
    }
  },
  { condition: notWhileReplaying },
);

/**
 * Sync game state with server — calls init and returns balance + progress.
 * Reusable for any transition point that needs fresh server state.
 * Pass { syncPots: true } and/or { syncBalance: true } to control what updates.
 */
export const syncGameState = createAsyncThunk(
  "gameSettings/syncGameState",
  async (options = {}, { rejectWithValue }) => {
    try {
      const result = await handleInitGame();
      return {
        balance: result.balance,
        progress: result.progress || null,
        syncPots: options.syncPots ?? false,
        syncBalance: options.syncBalance ?? false,
      };
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error.message);
    }
  },
  { condition: notWhileReplaying },
);

/**
 * Sync balance with server - calls provider's getBalance() endpoint
 * Lightweight: only fetches and updates balance, nothing else
 * Use after free spin outro, win animations, or anywhere balance needs server sync
 */
export const syncBalance = createAsyncThunk(
  "gameSettings/syncBalance",
  async (_, { rejectWithValue }) => {
    try {
      const { balance } = await handleGetBalance();
      return { balance };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
  { condition: notWhileReplaying },
);

/**
 * Sync free rounds total win with server — calls init and extracts freeRoundsTotalWin.
 * Lightweight: only updates FR displayedWin, nothing else.
 * Use after animated win counters finish during FR — server's value already includes current win.
 */
export const syncFreeRoundsWin = createAsyncThunk(
  "gameSettings/syncFreeRoundsWin",
  async (_, { rejectWithValue }) => {
    try {
      const result = await handleInitGame();
      return { freeRoundsTotalWin: result.freeRoundsTotalWin ?? 0 };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
  { condition: notWhileReplaying },
);

/**
 * Refresh game data - lightweight init without asset reloading
 * Used for error 205 (free rounds) to fetch updated game state
 * Does NOT reset spinSlice state, preserving spinComplete and autoplay
 * @param {Object} options - Options object
 * @param {boolean} options.useApiData - If true, uses fresh API data instead of stored data (for error recovery)
 * @param {boolean} options.ignoreFreeRoundsStorage - If true, ignores free rounds localStorage (for new batch activation)
 */
export const refreshGameData = createAsyncThunk(
  "gameSettings/refreshGameData",
  async (options = {}, { dispatch, rejectWithValue }) => {
    try {
      const { useApiData = false, ignoreFreeRoundsStorage = false } =
        typeof options === "boolean" ? { useApiData: options } : options;

      // API call only - RGS handles feature detection
      const result = await handleInitGame(useApiData, ignoreFreeRoundsStorage);

      // If free rounds are active after refresh, open InfoPopup
      // Skip when remaining === 0 (last round triggered bonus — popup is pointless)
      if (FEATURES_CONFIG.FREE_ROUNDS.ENABLED && result.freeRounds?.status === "active") {
        const performed = result.freeRounds.performed;
        const count = result.freeRounds.count;
        const remaining = count - performed;

        if (remaining > 0) {
          dispatch(openInfoPopup(
            performed === 0
              ? { key: "you_have_been_credited_with_free_rounds", data: { N: count } }
              : { key: "you_have_free_rounds_left", data: { N: remaining } }
          ));
        }
      }

      // Feature slices will react to universal state
      return { result };
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error.message);
    }
  },
  // ── NOT guarded, deliberately ──
  //
  // The three balance syncs above are pure bookkeeping and are safe to refuse.
  // This one is the ERROR RECOVERY VALVE: it is what clears `reelsFrozen` and
  // `playBlockedUntilResync`, and it is the only way out of a frozen board.
  // Refusing it inside a replay risked wedging the LIVE game shut behind a
  // replay's error, which is a far worse failure than the live `init` it would
  // have sent. Sending `init` is not a ban risk in itself — `init` is always in
  // available_actions — so the trade is not close.
);

// Initial state for the game settings slice
const initialState = {
  token: "",
  balance: 0,
  displayedBalance: 0, // What the UI should show
  bet: 0,
  baseBet: 1, // Base bet for paytable calculations (default to 1 to avoid division by zero)
  bets: [],
  currency: "",
  currencyConfig: {
    subunits: 100, // Number of subunits per main currency unit (default: 100 for cents/dollars)
    exponent: 2, // Number of decimal places to display (default: 2 for "10.00")
    symbol: "", // Currency symbol (e.g., "$", "€", "FUN")
  },
  paytable: [],
  rtp: null, // RTP value from API init (e.g., 96.5)
  bonusBuyMultiplier: null, // Bonus buy cost multiplier — provided by API init (feature_multipliers.bonus_buy)
  bonusBuyExtraMultiplier: null, // Bonus buy level 2 cost multiplier — provided by API init
  // Every bonus buy level the API advertises, as { level: multiplier }. The
  // two fields above are levels 1 and 2 of this same map, kept because the
  // primary UI still reads them. Mad Labs has four levels (GDD §10).
  bonusBuyMultipliers: {},
  // The API's own name for the buy feature, sent back as `purchased_feature`.
  // Zeus called it "bonus_buy"; do not assume that name for any other game.
  bonusBuyFeatureName: null,
  // { featureName: { level: multiplier } } — every buy the API sells
  // (Cleopatra's Fire: bonus_buy = Hold & Win, freespin_buy = free spins).
  bonusBuyFeatures: {},
  currentRound: {
    reels: [],
    wins: [],
    win: 0,
  },
  winningLines: [],
  status: "idle",
  error: null,
  errorCode: null, // Store error code from API (e.g., 301)
  errorDescription: null, // Store error description from API (e.g., "not_enough_money")
  // Universal state fields (provider-agnostic)
  gameMode: "base", // "base" | "feature" - universal game mode
  featureState: { type: null, phase: null, data: null }, // Universal feature state
  reloadState: { needsReload: false }, // Universal reload state

  // Legacy provider-specific fields (will be removed in Phase 11)
  availableActions: ["init", "spin"], // Track available actions from API (spin vs respin)
  flowState: "ready", // Track flow.state from API (ready, closed, freespins)
  features: null, // Track respin features: {respins_issued, respins_left, trigger_screen}
  lastCommand: null, // Track the command sent to API ("spin" or "respin")
  heartbeatError: {
    consecutiveFailures: 0,
    state: "none", // none, warning (3+ failures), critical (10+ failures)
  },
  freeRounds: null, // Casino-granted free rounds data from API
  jackpotMultipliers: null, // Raw multipliers from API: { mini: 15, minor: 50, major: 150, grand: 5000 }
  jackpotValues: { mini: 0, minor: 0, major: 0, grand: 0 }, // Jackpot display values (multiplier × bet)
  potProgress: null, // Zeus: POT stage data from API { D: { stage: N }, M: { stage: N }, C: { stage: N } }
  // Live round/feature state parked while a replay session borrows this slice.
  // null when not replaying. See the startReplaySession case in extraReducers.
  preReplaySnapshot: null,
};

// Helper: compute jackpot display values from bet and API multipliers
// When multipliers are provided (Zeus), uses API-driven values
// When multipliers are null (backward compat), falls back to symbolData
const computeJackpotValues = (bet, multipliers) => {
  if (!multipliers) {
    // Fallback: use symbolData values (backward compat for Noble Fruits)
    return {
      mini: (symbolData[27]?.value || 25) * (bet || 1),
      minor: (symbolData[28]?.value || 50) * (bet || 1),
      major: (symbolData[29]?.value || 150) * (bet || 1),
      grand: 0,
    };
  }
  return {
    mini: (multipliers.mini || 0) * (bet || 1),
    minor: (multipliers.minor || 0) * (bet || 1),
    major: (multipliers.major || 0) * (bet || 1),
    grand: (multipliers.grand || 0) * (bet || 1),
  };
};

// Create a slice for managing game settings
const gameSettingsSlice = createSlice({
  name: "gameSettings",
  initialState,
  reducers: {
    incrementBet(state) {
      const currentIndex = state.bets.indexOf(state.bet);
      if (currentIndex < state.bets.length - 1) {
        state.bet = state.bets[currentIndex + 1];
        const isFeatureActive = state.gameMode === "feature";
        if (!isFeatureActive) {
          if (state.currentRound?.reels?.length) {
            // CRITICAL: Preserve existing round data, only update bet and balance
            const existingBaseGame = StorageManager.loadBaseGameRound();
            const roundToSave = existingBaseGame?.round || state.currentRound;

            StorageManager.saveBaseGameRound({
              round: roundToSave,
              bet: state.bet,
              balance: state.balance,
            });
          }
        }
        state.jackpotValues = computeJackpotValues(state.bet, state.jackpotMultipliers);
      }
    },

    decrementBet(state) {
      const currentIndex = state.bets.indexOf(state.bet);
      if (currentIndex > 0) {
        state.bet = state.bets[currentIndex - 1];
        const isFeatureActive = state.gameMode === "feature";
        if (!isFeatureActive) {
          if (state.currentRound?.reels?.length) {
            // CRITICAL: Preserve existing round data, only update bet and balance
            const existingBaseGame = StorageManager.loadBaseGameRound();
            const roundToSave = existingBaseGame?.round || state.currentRound;

            StorageManager.saveBaseGameRound({
              round: roundToSave,
              bet: state.bet,
              balance: state.balance,
            });
          }
        }
        state.jackpotValues = computeJackpotValues(state.bet, state.jackpotMultipliers);
      }
    },

    setBet(state, action) {
      const betValue = action.payload;
      if (state.bets.includes(betValue)) {
        state.bet = betValue;
        const isFeatureActive = state.gameMode === "feature";
        if (!isFeatureActive) {
          if (state.currentRound?.reels?.length) {
            // CRITICAL: Preserve existing round data, only update bet and balance
            // This prevents overwriting good round data with H&W outro round (coins/blanks)
            const existingBaseGame = StorageManager.loadBaseGameRound();
            const roundToSave = existingBaseGame?.round || state.currentRound;

            StorageManager.saveBaseGameRound({
              round: roundToSave,
              bet: state.bet,
              balance: state.balance,
            });
          }
        }
        state.jackpotValues = computeJackpotValues(state.bet, state.jackpotMultipliers);
      }
    },

    setBaseBet(state, action) {
      state.baseBet = action.payload || 1;
    },

    setBalance(state, action) {
      state.balance = Math.max(0, action.payload);
      state.displayedBalance = Math.max(0, action.payload); // Also update displayedBalance so UI shows the change
    },

    setDisplayedBalance(state, action) {
      state.displayedBalance = Math.max(0, action.payload);
    },

    updateDisplayedBalance(state, action) {
      // BALANCE STRATEGY: This reducer syncs displayedBalance after win animations complete
      // Behavior depends on provider's balance strategy:
      // - If serverIncludesWin (Redstone): Just sync to balance (already has win)
      // - If !serverIncludesWin (PopiPlay): Add win to balance, then sync
      //
      // For now, just syncing to balance works for Redstone
      // PopiPlay adapter will handle win addition in its own flow

      if (action.payload !== undefined) {
        state.displayedBalance = Math.max(0, action.payload);
      } else {
        // Sync displayedBalance to current balance
        state.displayedBalance = Math.max(0, state.balance);
      }
    },

    setAvailableActions(state, action) {
      state.availableActions = action.payload;
    },

    setFlowState(state, action) {
      state.flowState = action.payload;
    },

    setLastCommand(state, action) {
      state.lastCommand = action.payload;
    },

    restoreSavedBet(state) {
      // Restore bet from base game round (with session validation)
      if (!StorageManager.isSessionValid()) return;
      const baseGame = StorageManager.loadBaseGameRound();
      const savedBet = baseGame?.bet;
      if (savedBet && state.bets.includes(savedBet)) {
        state.bet = savedBet;
      }
    },

    /**
     * Clear featureState when returning to base game
     * Dispatched from SceneTransitionOverlay when exiting H&W
     */
    clearFeatureStateToBase(state) {
      state.featureState = { type: null, phase: null, data: null };
      state.gameMode = "base";
    },

    /**
     * A LOST gamble hands the round back as the ordinary win it was.
     *
     * Dispatched by the gamble dialog the moment the loss is known, right after
     * its snapClearAll. The scatter entry is the trigger's own line, and the
     * lost package takes it and the trigger flags with it. What the round PAID
     * stays, and the win controller presents it again as a fresh round: show
     * all wins, then the cycle. Vladimir, 2026-09-24: *"right when we hit a
     * loss... bring back show all wins, except scatter... and if we have only
     * scatters and no win, then we clear everything."*
     *
     * The twin of the reload strip in PopiPlay initGame, which keeps the same
     * wins (`!isScatter`) for the same reason.
     *
     * `winAlreadyPresented` holds back only the CELEBRATION — it played once —
     * and updateDisplayedBalance is a sync, so the second pass cannot pay
     * twice. See useWinController's `isAnimatedWin`.
     *
     * No `settledGambleScatter` here: the reveal already painted the lost card
     * onto the cell, and `gambleScatter` has by now been patched to the loss
     * (0 / 0), so a picture built from it would be a blank window.
     */
    keepLostRoundMoney(state) {
      const round = state.currentRound;
      if (!round) return;
      const kept = (round.wins || []).filter((w) => !w?.isScatter);
      const keptWin = kept.reduce((total, w) => total + (w?.win || 0), 0);
      // ── Reduced EITHER way, money or not ──
      //
      // The trigger is gone whatever the round paid. A scatters-only loss used
      // to return here untouched, which left `triggerWin`, `triggerReveal`, the
      // scatter entry and a zeroed `gambleScatter` sitting on the live round —
      // exactly what a spin's pending used to clear before gamble commands
      // stopped clearing it. Stale, and a later replay exit re-running the
      // presentation would have opened the doors on an empty window. With no
      // money the reduced round has no wins and no triggerWin, so the win
      // controller returns at once and the cleared board stays cleared.
      // Vladimir, 2026-09-24: *"no buildups no stale states."*
      const paid = keptWin > 0;
      state.currentRound = {
        ...round,
        wins: kept,
        win: keptWin,
        triggerWin: paid,
        triggerReveal: false,
        gambleScatter: null,
        scatterCount: 0,
        scatterPositions: [],
        winType: paid ? round.winType || "" : "",
        isAnimatedWin: paid ? !!round.isAnimatedWin : false,
        winAlreadyPresented: true,
      };
    },

    /**
     * Set featureState to free rounds when exiting H&W back to free rounds
     * Next spin will use "spin" with bonus options
     */
    setFeatureStateFromFreeRounds(state, action) {
      const { betValue, betLevel, count, performed, totalWin } = action.payload;
      state.featureState = {
        type: "freeRounds",
        phase: "active",
        data: {
          betValue: betValue ?? 0,
          betLevel: betLevel ?? null,
          count: count ?? 0,
          performed: performed ?? 0,
          totalWin: totalWin ?? 0,
        },
      };
      state.gameMode = "feature";
    },

    /**
     * Update reel symbols when expanding wilds replace them
     * This ensures the Redux state reflects the wild replacements
     * so that next spin reads correct symbols
     */
    updateReelSymbols(state, action) {
      const { reelIndex, symbolReplacements } = action.payload;

      if (state.currentRound?.reels?.[reelIndex]) {
        // symbolReplacements is an array of { position, symbolId }
        symbolReplacements.forEach(({ position, symbolId }) => {
          state.currentRound.reels[reelIndex][position] = symbolId;
        });
      }
    },
  },
  extraReducers: (builder) => {
    // Required inline, not imported at the top: freeSpinsSlice requires THIS
    // slice while its own reducers are being built, so a top-level import here
    // closes the loop and one of the two receives `undefined`. Same reason
    // freeSpinsSlice and potCollectionSlice both do it this way.
    const { setFreeSpinsActive } = require("./freeSpinsSlice");

    builder
      // ── The room takes the screen: the base round's win is spent ──
      //
      // It was presented in the base game and paid there. Keeping it on the live
      // round through the whole bonus leaves a spin's worth of celebration armed
      // behind a copied board — win boxes and symbol animations for money the
      // player already collected — and every consumer then has to know not to
      // draw it.
      //
      // The same fields `rgsPlay.pending` clears on an ordinary spin, and the
      // same ones StorageManager.stripBaseGameWins clears in the STORED round
      // for exactly this reason ("so reload won't replay base game win
      // animations"). That twin has existed all along; this is the live half of
      // it, which was never done.
      //
      // REELS ARE KEPT. The board is what the room copies and what the base game
      // comes back to when the round is over.
      .addCase(setFreeSpinsActive, (state, action) => {
        if (!action.payload || !state.currentRound) return;
        state.currentRound.win = 0;
        state.currentRound.wins = [];
        state.currentRound.triggerWin = false;
        state.currentRound.winType = "";
        state.currentRound.isAnimatedWin = false;
        // ── The board behind the room is SETTLED from this moment ──
        //
        // The same two fields PopiPlay initGame stamps on a reload straight
        // into the room, so a live entry and a reload hand the same board
        // back. Without them only the reload path repainted the golden
        // composites and the settled card when the room closed: on a live
        // entry every touch of the hidden base cells repainted them by symbol
        // id (ReelControllerV2's id-paint drops a composite even for the same
        // id), and the player came back to twelve mystery lids where the
        // coins and magnets had been. Vladimir, 2026-09-24: *"it should have
        // same coins and elements inside as it had when we came into the
        // game."* Reel.jsx repaints from goldenShownBoard whenever a settled
        // round is shown again; the win and mystery controllers already treat
        // a settled board as presented.
        state.currentRound.boardSettled = true;
        state.currentRound.settledGambleScatter =
          state.currentRound.settledGambleScatter ||
          state.currentRound.gambleScatter ||
          null;
      })
      // ── Replay session entry: park the live round ──
      // A replay spin writes its recorded round straight into this slice
      // (see rgsPlay.fulfilled). Without stashing what was there first,
      // exiting replay leaves the live game showing the REPLAYED reels, and —
      // worse than the visual — a stale featureState/availableActions/
      // flowState/lastCommand, which getSpinParams reads to decide what the
      // next real spin sends. A replayed bonus round could leave the live
      // game asking the server for a respin it isn't in.
      //
      // Snapshot rather than clear, for the same reason as freeRoundsSlice:
      // the player may be mid-feature live and just watching an old round.
      // Only the first start snapshots — "Replay Again" dispatches this too,
      // and by then the slice holds replay data.
      .addCase(startReplaySession, (state) => {
        if (state.preReplaySnapshot) return;
        const snapshot = {};
        for (const field of REPLAY_SNAPSHOT_FIELDS) snapshot[field] = state[field];
        state.preReplaySnapshot = snapshot;
      })

      // ── Replay session exit: hand the live round back ──
      // Restores reels, feature state and the flow fields together, so the
      // player returns to precisely the board they left.
      .addCase(endReplaySession, (state) => {
        const snapshot = state.preReplaySnapshot;
        if (!snapshot) return;
        for (const field of REPLAY_SNAPSHOT_FIELDS) state[field] = snapshot[field];
        state.preReplaySnapshot = null;

        // ── The round coming back has ALREADY been celebrated ──
        //
        // The player watched their big win, opened the history, replayed some
        // other round, and is now put back where they were. Handing the round
        // back unmarked makes the win controller treat it as new and play the
        // whole tier chain a second time for money paid minutes ago.
        //
        // The controller already dedupes on `${roundId}-${winsHash}`, which
        // would have caught this — except that set is cleared by the visual
        // reset the replay runs on its way in and out. A flag that travels with
        // the DATA survives that; a ref does not. It clears itself too: the next
        // real spin brings a new round object without it.
        //
        // Only the CELEBRATION is withheld — see useWinController. Win lines,
        // win boxes, the amount and the balance all behave as normal.
        if (state.currentRound) {
          state.currentRound = {
            ...state.currentRound,
            winAlreadyPresented: true,
          };
        }
      })

      .addCase(initializeGame.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.isLoading = true;
      })
      .addCase(initializeGame.fulfilled, (state, action) => {
        const { result } = action.payload;

        // Simple assignment - no logic!
        // RGS has already handled all feature detection and storage operations
        state.token = result.token;
        state.balance = Math.max(0, result.balance);
        state.displayedBalance = Math.max(0, result.balance);

        state.bet = result.bet;
        // Override bet with locked FR bet when free rounds are active
        if (result.freeRounds?.bet_value) {
          state.bet = result.freeRounds.bet_value;
        }
        state.baseBet = result.baseBet || 1;
        state.bets = result.bets;
        state.currency = result.currency;
        state.paytable = result.paytable || [];
        state.rtp = result.rtp || window.__OPTIONS__?.math?.rtp?.main || null;
        state.winningLines = result.winningLines || [];

        // Store currency configuration
        if (result.currencyConfig) {
          state.currencyConfig = {
            subunits:
              result.currencyConfig.subunits !== undefined
                ? result.currencyConfig.subunits
                : 100,
            exponent:
              result.currencyConfig.exponent !== undefined
                ? result.currencyConfig.exponent
                : 2,
            symbol: result.currencyConfig.symbol || "",
          };
        }

        // Set bonus buy multipliers if provided
        if (result.bonusBuyMultiplier !== undefined) {
          state.bonusBuyMultiplier = result.bonusBuyMultiplier;
        }
        if (result.bonusBuyExtraMultiplier !== undefined) {
          state.bonusBuyExtraMultiplier = result.bonusBuyExtraMultiplier;
        }
        if (result.bonusBuyMultipliers !== undefined) {
          state.bonusBuyMultipliers = result.bonusBuyMultipliers || {};
        }
        if (result.bonusBuyFeatureName !== undefined) {
          state.bonusBuyFeatureName = result.bonusBuyFeatureName || null;
        }
        if (result.bonusBuyFeatures !== undefined) {
          state.bonusBuyFeatures = result.bonusBuyFeatures || {};
        }

        // Store jackpot multipliers from API and compute display values
        state.jackpotMultipliers = result.jackpotMultipliers || null;
        state.jackpotValues = computeJackpotValues(state.bet, state.jackpotMultipliers);

        // Set current round if provided
        if (result.round) {
          state.currentRound = result.round;
        }

        // Universal state fields (provider-agnostic)
        state.gameMode = result.gameMode || "base";
        state.featureState = result.featureState || {
          type: null,
          phase: null,
          data: null,
        };
        state.reloadState = result.reloadState || { needsReload: false };

        // ── Win replay balance withholding on reload ──
        // Server balance already includes the win, but the win animation /
        // completion popup will replay from localStorage. Subtract the pending
        // win from displayedBalance so the reveal feels authentic.
        // The close flow (finishAnimation / useFreeRoundsCompletion / SceneTransition)
        // syncs displayedBalance back to real balance when done.
        //
        // Priority: FR completion > feature outro > base game animated win
        // (FR total already includes individual spin wins — no double-counting)
        const isFeatureActive = state.gameMode === "feature";
        const savedFR = StorageManager.loadFreeRoundsRound();
        const frJustCompleted = savedFR && savedFR.performed >= savedFR.count && savedFR.win > 0;
        // FR still in progress — don't touch balance at all.
        // Individual FR spin wins don't affect displayedBalance mid-session.
        const frStillActive = savedFR && savedFR.performed < savedFR.count;

        if (frStillActive) {
          // Free Rounds in progress: balance stays as-is
        } else if (frJustCompleted) {
          // Free Rounds completed: total FR win replays via completion popups.
          // syncGameState in useFreeRoundsCompletion restores displayedBalance.
          state.displayedBalance = Math.max(0, state.displayedBalance - savedFR.win);
        } else if (!isFeatureActive) {
          // ── Base game: ANY win the reload replays is withheld ──
          //
          // It used to be the big win only. An ordinary win stayed in the
          // displayed balance, so the replay showed the win while the balance
          // already held it and nothing ever moved. Now every restored win is
          // taken off and the replay pays it back — `updateDisplayedBalance`
          // on the win's own edge (WinDisplay) for an ordinary win, the
          // celebration's finish for a big one — both SYNC to the real balance,
          // so this can never double-credit.
          //
          // Read off the round actually restored — what will be presented.
          //
          // NOT when a free-spins completion is being withheld below: that
          // total already contains the trigger spin's win, so taking this one
          // off as well would take it twice. And NOT while the server still
          // owes a feature — the wallet does not hold that round's money yet.
          const replayed = state.currentRound;
          const featureOwed = (result.availableActions || []).some(
            (a) => a !== "init" && a !== "spin",
          );
          const savedFreeSpinsForBase = StorageManager.loadFreeSpinsRound();
          const fsCompletionWithheld =
            !!savedFreeSpinsForBase?.closed && savedFreeSpinsForBase.totalWin > 0;
          if (
            !featureOwed &&
            !fsCompletionWithheld &&
            // A settled board does not present again — nothing to pay back.
            !replayed?.boardSettled &&
            replayed?.triggerWin &&
            replayed.win > 0
          ) {
            state.displayedBalance = Math.max(0, state.displayedBalance - replayed.win);
          }
        } else {
          // Feature (bonus outro): totalWin > 0 means last respin is done,
          // outro win animation will replay on reload
          const savedBonusGame = StorageManager.loadBonusGameRound();
          if (savedBonusGame?.totalWin > 0) {
            state.displayedBalance = Math.max(0, state.displayedBalance - savedBonusGame.totalWin);
          }
        }

        // ── Free spins outro: withhold the round's total the same way ──
        //
        // A reload after the last free spin replays the total win dialog from
        // the stored record, and the balance the server sends already contains
        // that win. Left alone, the player watches a counter reveal a total
        // their balance has already been paid — the exact reason the bonus
        // branch above subtracts. The wipe's syncGameState puts it back on the
        // far side, as it does live.
        //
        // Outside the if/else chain above because free spins reports gameMode
        // "feature" mid-round and "base" once the round has closed, so neither
        // branch reliably owns this case; the stored record does.
        const savedFreeSpins = StorageManager.loadFreeSpinsRound();
        if (savedFreeSpins?.closed && savedFreeSpins.totalWin > 0) {
          state.displayedBalance = Math.max(
            0,
            state.displayedBalance - savedFreeSpins.totalWin,
          );
        }

        // Provider-specific fields (for backward compatibility during migration)
        state.availableActions = result.availableActions || ["init", "spin"];
        state.flowState = result.flowState || "ready";
        state.features = result.features || null;
        state.freeRounds = result.freeRounds || null;

        // Zeus: POT progress stages from API (used to restore pots on init)
        state.potProgress = result.progress?.pots || null;

        if (result.command) {
          state.lastCommand = result.command;
        }

        state.status = "succeeded";
        state.isLoading = false;
      })
      .addCase(initializeGame.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to initialize the game";
        state.isLoading = false;
      })

      // Listen for spin actions from spinSlice
      .addCase(rgsPlay.pending, (state, action) => {
        // Pessimistic update: Don't deduct balance until we get successful response
        // Just set status and clear previous wins

        // ── A decision (the free-spins gamble) is not a spin ──
        //
        // The same rule `fulfilled` below and spinSlice's pending follow: PLAY
        // and COLLECT on the gamble bet nothing and start no round, so the
        // round on screen is not "previous". Cleared here, its win went to 0
        // the moment the button was pressed and the Win row dropped with it —
        // the latch in WinDisplay lets go at `activeRoundWin <= 0`. Vladimir,
        // 2026-09-24: *"it should stay there, and then get covered by
        // transition and switch to total win."* On a loss the player keeps
        // that win too, so it has to still be there when the dialog closes.
        const isDecision =
          kindOfSpinArg(action.meta?.arg) === COMMAND_KIND.DECISION;

        // Clear all round data but KEEP reels for error recovery
        // GUARD: Don't touch base game currentRound during feature respins
        const isFeatureMode = state.gameMode === "feature";
        if (state.currentRound && !isFeatureMode && !isDecision) {
          state.currentRound.win = 0;
          state.currentRound.wins = [];
          state.currentRound.scatterCount = 0;
          state.currentRound.scatterPositions = [];
          state.currentRound.triggerWin = false;
          state.currentRound.winType = "";
          state.currentRound.isAnimatedWin = false;
          state.currentRound.anticipation = false;
          // The room's "already played" stamp (setFreeSpinsActive) belongs to
          // the round it settled. A replay's "Replay Again" spins the SAME id,
          // and a stamp carried into it made the Golden Mystery read "settled
          // board, not replayed" and skip on every pass after the first.
          state.currentRound.boardSettled = false;
          state.currentRound.settledGambleScatter = null;
          // DO NOT clear state.currentRound.reels - keep for error recovery!
        }

        state.error = null;
        state.status = "spinning";
      })
      .addCase(rgsPlay.fulfilled, (state, action) => {
        const {
          token,
          bet,
          balance,
          round,
          availableActions,
          flowState,
          features: featuresPayload,
          command,
          freeRounds: freeRoundsData,
          bonusCost,
          gameMode,
          featureState,
        } = action.payload;

        // ── A decision (the free-spins gamble) is not a spin ──
        //
        // Its payload has no board — outcome.screen is null — so absorbing it
        // like a spin overwrote the live trigger round with an empty one and
        // ran the base-spin bookkeeping (bet deduction included) for a
        // command that bet nothing. Only what the response actually carries
        // may land: balance, the server's view of what is allowed next, and
        // the round's OWN gamble data — merged into the round on screen,
        // never replacing it. freeSpinsSlice reads the ladder off the same
        // payload; nothing else here is its business.
        if (kindOfCommand(command) === COMMAND_KIND.DECISION) {
          if (balance !== undefined) {
            state.balance = Math.max(0, balance);
          }
          state.features = featuresPayload || state.features;
          state.availableActions = availableActions || state.availableActions;
          state.flowState = flowState || state.flowState;
          state.lastCommand = command;

          // The one piece of gamble data the round carries: what the scatter
          // reveal settled on. Kept current so a reveal replayed later shows
          // the rung the player actually reached, not the first offer.
          const gamble = action.payload.gamble;
          if (
            state.currentRound?.gambleScatter &&
            Array.isArray(gamble?.current)
          ) {
            const [value, tier] = gamble.current;
            state.currentRound.gambleScatter = {
              ...state.currentRound.gambleScatter,
              value,
              tier,
              variants: gamble.variants ?? [],
            };
          }
          state.status = "succeeded";
          return;
        }

        // Simple state updates - RGS has handled all storage operations
        if (token && token !== state.token) {
          state.token = token;
        }

        state.bet = bet !== undefined ? bet : state.bet;

        // BALANCE STRATEGY: Get provider-specific balance management strategy
        const balanceStrategy = handleGetBalanceStrategy();

        // GAME RULE: Determine if bet should be deducted (never during features)
        //
        // ── The COMMAND decides, not gameMode ──
        //
        // gameMode comes from detectFeatureState, which only knows the respin
        // bonus. Pot Collection is therefore "feature" and is protected; a
        // Mad Labs `freespin` has no featureState at all and arrived here as
        // "base", so every free spin deducted a bet that was never charged and
        // wrote the result straight into displayedBalance — the balance moving
        // mid-feature, which no bonus in this game may do.
        //
        // spinCommands already classifies both of them as FEATURE. Ask it, and
        // any future feature spin is covered the day its command is added there
        // rather than the day someone teaches detectFeatureState about it.
        const isBaseGameSpin =
          kindOfCommand(command) === COMMAND_KIND.BASE && gameMode === "base";

        // Check if free rounds just ended: was "played", now null (localStorage cleared)
        const wasFreeRoundsPlayed = state.freeRounds?.status === "played";
        const isFreeRoundsNowNull =
          freeRoundsData?.status === null || freeRoundsData === null;
        const freeRoundsJustEnded = wasFreeRoundsPlayed && isFreeRoundsNowNull;

        const isFreeRoundsActive =
          state.freeRounds?.status === "active" ||
          (state.freeRounds?.status === "played" && !freeRoundsJustEnded);

        // Apply balance strategy only for base game spins (not during features)
        const shouldDeductBet = isBaseGameSpin && !isFreeRoundsActive;

        if (shouldDeductBet && balanceStrategy.clientDeductsBet) {
          const deductionAmount =
            bonusCost !== undefined
              ? bonusCost
              : bet !== undefined
                ? bet
                : state.bet;
          state.balance = Math.max(0, state.balance - deductionAmount);
          state.displayedBalance = state.balance;
        }

        // Store the round — ONLY for base game spins
        // Feature games (RC, H&W) have their own round state in featureGameSettingsSlice
        //
        // ── The COMMAND decides here too ──
        //
        // Same defect, same fix, as the bet deduction twenty lines up: a Mad
        // Labs `freespin` has no featureState, so `gameMode` arrives as "base"
        // and the FREE SPIN'S BOARD was written straight over the base round.
        // Nothing showed it during the feature — the room draws from
        // freeSpins.currentRound — but the base reels repaint from this on the
        // way back, so the player returned from the bonus to a base game
        // covered in the bonus's Golden Mysteries. Vladimir, 2026-09-23:
        // *"when we came back from free spins, i had bunch of those same golden
        // mysteries on my base game, and we should have the initial screen
        // which started the game."*
        //
        // The rule is not new and this is the only place that was not applying
        // it. PopiPlay/RGS already guards the STORED copy with exactly this
        // test — "Only a BASE command may save" — which is why a RELOAD came
        // back correct while the live screen did not. And spinCommands states
        // it outright for FEATURE: "never saved as the base round".
        //
        // DECISION never reaches this line; it returns at the top of the
        // handler. So this adds `freespin` and any future feature spin.
        const isFeatureSpin =
          kindOfCommand(command) !== COMMAND_KIND.BASE ||
          gameMode === "feature" ||
          state.gameMode === "feature";
        if (round && !isFeatureSpin) {
          if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "golden_mult") {
            // One multiplier, no collector. Synthetic, so the win is rescaled
            // with the stake — the same rule golden_big follows, and for the
            // same reason: only the verbatim capture keeps the server's numbers.
            const multBet = state.bet || DEV_GOLDEN_MULT_ROUND.bet;
            const multWin =
              (DEV_GOLDEN_MULT_ROUND.win / DEV_GOLDEN_MULT_ROUND.bet) * multBet;
            state.currentRound = {
              ...DEV_GOLDEN_MULT_ROUND,
              bet: multBet,
              win: multWin,
              wins: DEV_GOLDEN_MULT_ROUND.wins.map((w) => ({
                ...w,
                win: multWin,
              })),
              id: `dev-golden-mult-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "golden_big") {
            // The wide chain. Synthetic, so the win IS rescaled with the stake —
            // unlike the verbatim capture beside it, whose whole value is that
            // its numbers are the server's.
            const bigBet = state.bet || DEV_GOLDEN_BIG_ROUND.bet;
            const bigWin =
              (DEV_GOLDEN_BIG_ROUND.win / DEV_GOLDEN_BIG_ROUND.bet) * bigBet;
            state.currentRound = {
              ...DEV_GOLDEN_BIG_ROUND,
              bet: bigBet,
              win: bigWin,
              wins: DEV_GOLDEN_BIG_ROUND.wins.map((w) => ({ ...w, win: bigWin })),
              id: `dev-golden-big-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "mystery_short") {
            // The small mystery board, for the 500ms build-up. Rebuilt per spin
            // for the same two reasons every simulated round is: the win tier is
            // derived from win / bet, and useWinController dedupes by round id.
            state.currentRound = {
              ...DEV_MYSTERY_SHORT_ROUND,
              bet: state.bet || DEV_MYSTERY_SHORT_ROUND.bet,
              id: `dev-mystery-short-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "golden_all") {
            // Every symbol type in one spin. The coin values are the round's own
            // and the payout is sum x bet, so the win moves honestly with the
            // stake — the same rule the other golden rounds follow.
            const allBet = state.bet || DEV_GOLDEN_ALL_ROUND.bet;
            const allWin =
              (DEV_GOLDEN_ALL_ROUND.win / DEV_GOLDEN_ALL_ROUND.bet) * allBet;
            state.currentRound = {
              ...DEV_GOLDEN_ALL_ROUND,
              bet: allBet,
              win: allWin,
              wins: DEV_GOLDEN_ALL_ROUND.wins.map((w) => ({
                ...w,
                win: allWin,
              })),
              id: `dev-golden-all-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "golden_blank") {
            // Round 1451830's shape: a board SC, a second SC from the feature,
            // and a BLANK SC1 from the feature. The coins rescale with the
            // stake like every other golden round; the scatter awards nothing,
            // so there is nothing else to move.
            const blankBet = state.bet || DEV_GOLDEN_BLANK_ROUND.bet;
            const blankWin =
              (DEV_GOLDEN_BLANK_ROUND.win / DEV_GOLDEN_BLANK_ROUND.bet) *
              blankBet;
            state.currentRound = {
              ...DEV_GOLDEN_BLANK_ROUND,
              bet: blankBet,
              win: blankWin,
              // Only the COIN payout rescales. The synthetic scatter win pays 0
              // and must stay 0 — it exists to carry the reveal's position.
              wins: DEV_GOLDEN_BLANK_ROUND.wins.map((w) =>
                w.isScatter ? w : { ...w, win: blankWin },
              ),
              id: `dev-golden-blank-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (
            DEV_SIMULATION_ENABLED &&
            DEV_SIMULATION_MODE === "golden_reroll_award"
          ) {
            // The same round, awarding on the scatter's last turn: a 951x win
            // AND a free-spins trigger in one spin. Wins present first, then
            // the scatters light and the gamble screen takes over.
            const awardRerollBet =
              state.bet || DEV_GOLDEN_REROLL_AWARD_ROUND.bet;
            const awardRerollWin =
              (DEV_GOLDEN_REROLL_AWARD_ROUND.win /
                DEV_GOLDEN_REROLL_AWARD_ROUND.bet) *
              awardRerollBet;
            state.currentRound = {
              ...DEV_GOLDEN_REROLL_AWARD_ROUND,
              bet: awardRerollBet,
              win: awardRerollWin,
              wins: DEV_GOLDEN_REROLL_AWARD_ROUND.wins.map((w) =>
                w.isScatter ? w : { ...w, win: awardRerollWin },
              ),
              id: `dev-golden-reroll-award-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "golden_reroll") {
            // Round 1472920 verbatim. Three magnets, a x2, a plain scatter and
            // a blank SC1 the server re-lists on every iteration.
            //
            // The coins rescale with the stake like every other golden round,
            // so the payout stays 951 x bet and `verifyGoldenScript` keeps
            // agreeing with it at any bet — which is the whole reason to have
            // this round on tap.
            const rerollBet = state.bet || DEV_GOLDEN_REROLL_ROUND.bet;
            const rerollWin =
              (DEV_GOLDEN_REROLL_ROUND.win / DEV_GOLDEN_REROLL_ROUND.bet) *
              rerollBet;
            state.currentRound = {
              ...DEV_GOLDEN_REROLL_ROUND,
              bet: rerollBet,
              win: rerollWin,
              // Only the COIN payout rescales. The scatter entry pays 0 and
              // must stay 0 — it carries positions, not money.
              wins: DEV_GOLDEN_REROLL_ROUND.wins.map((w) =>
                w.isScatter ? w : { ...w, win: rerollWin },
              ),
              id: `dev-golden-reroll-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "golden_award") {
            // The same round as `golden_blank`, but SC1 awards 8 spins at
            // level 3. The sequence it exercises: coins pay -> their glow goes
            // -> the THREE scatters light together -> SC1 opens and lands on
            // the package -> the gamble screen takes over.
            const awardBet = state.bet || DEV_GOLDEN_AWARD_ROUND.bet;
            const awardWin =
              (DEV_GOLDEN_AWARD_ROUND.win / DEV_GOLDEN_AWARD_ROUND.bet) *
              awardBet;
            state.currentRound = {
              ...DEV_GOLDEN_AWARD_ROUND,
              bet: awardBet,
              win: awardWin,
              // Only the COIN payout rescales — the trigger win pays 0 and
              // carries positions, not money.
              wins: DEV_GOLDEN_AWARD_ROUND.wins.map((w) =>
                w.isScatter ? w : { ...w, win: awardWin },
              ),
              id: `dev-golden-award-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "golden_gamble") {
            // The gamble-scatter branch. The coin values are the round's own and
            // the payout is sum x bet, so the win moves honestly with the stake
            // — the same rule the other golden rounds follow. Only the id is
            // regenerated, because useWinController dedupes on it.
            const gambleBet = state.bet || DEV_GOLDEN_GAMBLE_ROUND.bet;
            const gambleWin =
              (DEV_GOLDEN_GAMBLE_ROUND.win / DEV_GOLDEN_GAMBLE_ROUND.bet) *
              gambleBet;
            state.currentRound = {
              ...DEV_GOLDEN_GAMBLE_ROUND,
              bet: gambleBet,
              win: gambleWin,
              wins: DEV_GOLDEN_GAMBLE_ROUND.wins.map((w) => ({
                ...w,
                win: gambleWin,
              })),
              id: `dev-golden-gamble-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (
            DEV_SIMULATION_ENABLED &&
            DEV_SIMULATION_MODE === "golden_gamble_two"
          ) {
            // Two gamble scatters in one feature — see the round. Same rescale
            // rule as the single-scatter branch above: the coin values are the
            // round's and the payout is sum x bet, so the win follows the stake.
            const twoBet = state.bet || DEV_GOLDEN_GAMBLE_TWO_ROUND.bet;
            const twoWin =
              (DEV_GOLDEN_GAMBLE_TWO_ROUND.win / DEV_GOLDEN_GAMBLE_TWO_ROUND.bet) *
              twoBet;
            state.currentRound = {
              ...DEV_GOLDEN_GAMBLE_TWO_ROUND,
              bet: twoBet,
              win: twoWin,
              wins: DEV_GOLDEN_GAMBLE_TWO_ROUND.wins.map((w) => ({
                ...w,
                win: twoWin,
              })),
              id: `dev-golden-gamble-two-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "golden_chain") {
            // Round 1431382, verbatim. The win is NOT rescaled to the selected
            // stake: every number in this round is the server's, and the whole
            // point of it is that 80 is arithmetic we can check. Change the
            // stake in the UI and it stays 80, which is honest — it is a
            // recording, not a simulation.
            state.currentRound = {
              ...DEV_GOLDEN_CHAIN_ROUND,
              id: `dev-chain-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (
            DEV_SIMULATION_ENABLED &&
            (DEV_SIMULATION_MODE === "golden_mystery" ||
              DEV_SIMULATION_MODE === "golden_quiet" ||
              DEV_SIMULATION_MODE === "golden_full" ||
              DEV_SIMULATION_MODE === "golden_max")
          ) {
            // Four boards, one branch — the same round in four sizes. The quiet
            // one pays 16x so it stays under the big-win threshold; the full one
            // is mystery_reveal's fourteen coords turned golden; the max one is
            // every cell on the board, for measuring rather than for watching.
            // See each round for what it is for.
            const goldenSource =
              DEV_SIMULATION_MODE === "golden_quiet"
                ? DEV_GOLDEN_QUIET_ROUND
                : DEV_SIMULATION_MODE === "golden_full"
                  ? DEV_GOLDEN_FULL_ROUND
                  : DEV_SIMULATION_MODE === "golden_max"
                    ? DEV_GOLDEN_MAX_ROUND
                    : DEV_GOLDEN_MYSTERY_ROUND;
            // The B1 -> B2 board. Same two reasons as the mystery round below
            // for rebuilding it per spin: the win tier is derived from
            // win / bet, and useWinController dedupes by round id.
            //
            // The coin values are NOT rescaled to the selected stake. They are
            // the capture's own, and the payout is sum x bet, so changing the
            // stake in the UI moves the win honestly with it — see
            // DEV_GOLDEN_MYSTERY_ROUND.
            const goldenBet = state.bet || goldenSource.bet;
            const goldenWin =
              (goldenSource.win / goldenSource.bet) * goldenBet;
            state.currentRound = {
              ...goldenSource,
              bet: goldenBet,
              win: goldenWin,
              wins: goldenSource.wins.map((w) => ({
                ...w,
                win: goldenWin,
              })),
              id: `dev-golden-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "mystery_reveal") {
            // The mystery board. Rebuilt per spin for the same two reasons the
            // other simulated rounds are: the win tier is derived from
            // win / bet, so a hardcoded bet stops matching the moment the
            // player changes stake, and useWinController dedupes by round id,
            // so a fixed id plays once and every later spin is skipped.
            //
            // The wins are NOT rescaled to the selected bet: they are the real
            // amounts the server paid at bet 10, and the reveal is what is
            // being tested, not the tier chain. Change the stake and the tier
            // moves honestly with it.
            state.currentRound = {
              ...DEV_MYSTERY_ROUND,
              bet: state.bet || DEV_MYSTERY_ROUND.bet,
              id: `dev-mystery-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "win_trigger") {
            // Lines AND a trigger. The win is rescaled to the live stake for
            // the same reason big_win is: the tier comes from win / bet, so a
            // fixed amount lands on the wrong tier the moment the stake
            // changes — and the whole point of this round is WHICH path the
            // win takes. DEV_WIN_TRIGGER_MULTIPLIER is the knob; 8 is a plain
            // win, above 20 becomes a celebration.
            //
            // The scatter entry pays 0 and must stay at 0, so only the LINE
            // wins share the amount out between them.
            const wtBet = state.bet || DEV_WIN_TRIGGER_ROUND.bet;
            const wtWin = Math.round(wtBet * DEV_WIN_TRIGGER_MULTIPLIER);
            const wtLines = DEV_WIN_TRIGGER_ROUND.wins.filter(
              (w) => !w.isScatter,
            ).length;
            state.currentRound = {
              ...DEV_WIN_TRIGGER_ROUND,
              bet: wtBet,
              win: wtWin,
              wins: DEV_WIN_TRIGGER_ROUND.wins.map((w) =>
                w.isScatter ? w : { ...w, win: Math.round(wtWin / wtLines) },
              ),
              id: `dev-win-trigger-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "scatter_blank") {
            // The same trigger board landing on nothing — see
            // DEV_SCATTER_BLANK_ROUND. Rebuilt per spin for the one reason the
            // trigger round is: useWinController dedupes on the id, so a fixed
            // one plays once and every later spin is skipped.
            state.currentRound = {
              ...DEV_SCATTER_BLANK_ROUND,
              bet: state.bet || DEV_SCATTER_BLANK_ROUND.bet,
              id: `dev-scatter-blank-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "scatter_anticipation") {
            // Cleopatra's Fire: the anticipation board — see
            // DEV_SCATTER_ANTICIPATION_ROUND. No win and no trigger. Only the
            // id is regenerated, for the dedupe reason the others give.
            state.currentRound = {
              ...DEV_SCATTER_ANTICIPATION_ROUND,
              bet: state.bet || DEV_SCATTER_ANTICIPATION_ROUND.bet,
              id: `dev-scatter-anticipation-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "hp_rows") {
            // Cleopatra's Fire: one high pay across the middle two rows — see
            // DEV_HP_ROWS_ROUND. Positions only; no win, no trigger.
            state.currentRound = {
              ...DEV_HP_ROWS_ROUND,
              bet: state.bet || DEV_HP_ROWS_ROUND.bet,
              id: `dev-hp-rows-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "coin_trails") {
            // Cleopatra's Fire: three coins every spin, for the logo trail —
            // see DEV_COIN_TRAILS_ROUND. No win, no trigger; new id per spin.
            state.currentRound = {
              ...DEV_COIN_TRAILS_ROUND,
              bet: state.bet || DEV_COIN_TRAILS_ROUND.bet,
              id: `dev-coin-trails-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "jackpot_coins") {
            // Cleopatra's Fire: three jackpot coins every spin, for the plates'
            // hit clips — see DEV_JACKPOT_COINS_ROUND. No win, no trigger.
            state.currentRound = {
              ...DEV_JACKPOT_COINS_ROUND,
              bet: state.bet || DEV_JACKPOT_COINS_ROUND.bet,
              id: `dev-jackpot-coins-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "coin_anticipation") {
            // Cleopatra's Fire: the coin anticipation board — see
            // DEV_COIN_ANTICIPATION_ROUND. No win, no trigger; new id per spin.
            state.currentRound = {
              ...DEV_COIN_ANTICIPATION_ROUND,
              bet: state.bet || DEV_COIN_ANTICIPATION_ROUND.bet,
              id: `dev-coin-anticipation-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED && DEV_SIMULATION_MODE === "scatter_trigger") {
            // The trigger board. Nothing is scaled by bet — the trigger spin
            // pays nothing, and the awarded spin count comes from the round's
            // own reveal payload rather than from win / bet. Only the id is
            // regenerated, because useWinController dedupes on it and a fixed
            // id would let the sequence play exactly once.
            state.currentRound = {
              ...DEV_SCATTER_TRIGGER_ROUND,
              bet: state.bet || DEV_SCATTER_TRIGGER_ROUND.bet,
              id: `dev-scatter-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else if (DEV_SIMULATION_ENABLED) {
            // Rebuild per spin rather than reusing the exported object.
            //
            // Two things break with a static round. The win tier is derived
            // from win / bet, so a hard-coded bet stops matching the moment
            // the player changes their stake and the multiplier lands on the
            // wrong tier — or on none. And useWinController dedupes by round
            // id, so a fixed id means the celebration plays ONCE and every
            // later spin is silently skipped.
            const simBet = state.bet || DEV_SIMULATION_ROUND.bet;
            const simWin = Math.round(simBet * DEV_WIN_MULTIPLIER);
            state.currentRound = {
              ...DEV_SIMULATION_ROUND,
              bet: simBet,
              win: simWin,
              wins: DEV_SIMULATION_ROUND.wins.map((w) => ({
                ...w,
                win: Math.round(simWin / DEV_SIMULATION_ROUND.wins.length),
              })),
              id: `dev-sim-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
          } else {
            state.currentRound = round;
          }
        }

        // Store balance from API response
        // Strategy determines how balance is handled:
        // - If serverIncludesWin: balance already has win, sync after animations
        // - If !serverIncludesWin: balance is old, win will be added client-side after animations
        if (balance !== undefined) {
          state.balance = Math.max(0, balance);
          // displayedBalance stays at deducted amount until WinDisplay updates it
        }

        // Universal state fields (provider-agnostic)
        state.gameMode = gameMode || "base";
        state.featureState = featureState || {
          type: null,
          phase: null,
          data: null,
        };

        // Provider-specific fields (for backward compatibility)
        state.features = featuresPayload || null;
        state.availableActions = availableActions || ["init", "spin"];
        state.flowState = flowState || "ready";
        state.freeRounds = freeRoundsData || null;

        // Zeus: update POT progress from spin response
        const progressPayload = action.payload.progress;
        if (progressPayload?.pots) {
          state.potProgress = progressPayload.pots;
        }

        if (command) {
          state.lastCommand = command;
        }

        state.status = "succeeded";
      })
      .addCase(rgsPlay.rejected, (state, action) => {
        const errorData = action.payload;

        /* ============================================================
           DISABLED: Emergency Outcome Handling (Fake Spin Logic)

           Previously, RGS would generate fake non-winning combinations
           for errors. This logic is kept for backward compatibility but
           emergencyOutcome is no longer generated in RGS.js.

           All errors now freeze reels mid-spin via ReelControlContext.js
           instead of landing on fake patterns.
           ============================================================ */

        // Check for emergency outcome (legacy - no longer generated)
        if (errorData?.emergencyOutcome) {
          state.currentRound = {
            reels: errorData.emergencyOutcome.reels,
            wins: errorData.emergencyOutcome.wins || {},
            win: errorData.emergencyOutcome.win || 0,
            bet: errorData.emergencyOutcome.bet || state.bet,
          };
        }
        // No emergency outcome - reels freeze mid-spin instead

        // PESSIMISTIC UPDATE: No need to restore balance since we never deducted it
        // If API provides balance in error response, use it (for consistency)
        if (errorData?.balance !== undefined && errorData.balance !== null) {
          state.balance = Math.max(0, errorData.balance);
          state.displayedBalance = Math.max(0, errorData.balance);
        }
        // Otherwise, balance remains unchanged (as it should be)

        // Parse error response: {"errors":[{"code":301,"desc":"not_enough_money"}]}
        if (
          errorData?.errors &&
          Array.isArray(errorData.errors) &&
          errorData.errors.length > 0
        ) {
          const firstError = errorData.errors[0];
          state.errorCode = firstError.code;
          state.errorDescription = firstError.desc;
          state.error = `Error ${firstError.code}: ${firstError.desc}`;

          // ── A refused action invalidates the action list ──
          //
          // 202 unavailable_action means the server no longer allows what we
          // just sent, so `availableActions` — last written by a SUCCESS path —
          // is stale by definition. Every feature send-site checks that list
          // before sending (spinSlice's freespin guard, RGS.gambleAction,
          // GambleDialogController's own copy), and each of them was still
          // being told yes. A second refusal is `104 locked`: the session dies,
          // `init` included.
          //
          // ["init"], never []. The base-spin guard is deliberately permissive
          // on an empty list and would wave a spin straight through; `init` is
          // the one action that is always legal and is exactly what the
          // recovery flow needs to send next.
          if (firstError.code === 202) {
            state.availableActions = ["init"];
          }
        } else {
          state.error = errorData?.message || errorData || "Spin failed";
          state.errorCode = null;
          state.errorDescription = null;
        }

        state.status = "failed";
      })

      // Handle buy feature response
      .addCase(rgsBuyFeature.fulfilled, (state, action) => {
        const { balance } = action.payload;
        if (balance !== undefined) {
          state.balance = Math.max(0, balance);
          state.displayedBalance = Math.max(0, balance);
        }
        state.status = "idle";
      })
      .addCase(rgsBuyFeature.rejected, (state, action) => {
        state.error = action.payload || "Buy feature failed";
        state.status = "failed";
      })

      // Handle heartbeat sync - update ONLY balance
      .addCase(heartbeatSync.fulfilled, (state, action) => {
        const { balance } = action.payload;

        // CRITICAL: Update ONLY balance to avoid unnecessary re-renders
        // Do NOT update any other fields even if they exist in the response
        if (balance !== undefined) {
          state.balance = Math.max(0, balance);
          // Also update displayedBalance so UI reflects the change immediately
          state.displayedBalance = Math.max(0, balance);
        }

        // Reset error state on successful heartbeat
        state.heartbeatError.consecutiveFailures = 0;
        state.heartbeatError.state = "none";
      })
      .addCase(heartbeatSync.rejected, (state, action) => {
        // Increment consecutive failure counter
        state.heartbeatError.consecutiveFailures += 1;

        // Update error state based on failure count
        if (state.heartbeatError.consecutiveFailures >= 10) {
          state.heartbeatError.state = "critical";
        } else if (state.heartbeatError.consecutiveFailures >= 3) {
          state.heartbeatError.state = "warning";
        }

        // Don't set game status to failed - heartbeat errors should not block gameplay
      })

      // Handle syncGameState - selectively updates balance and/or pot progress
      .addCase(syncGameState.fulfilled, (state, action) => {
        const { balance, progress, syncPots, syncBalance } = action.payload;

        if (syncBalance && balance !== undefined) {
          state.balance = Math.max(0, balance);
          state.displayedBalance = Math.max(0, balance);
        }

        if (syncPots && progress?.pots) {
          state.potProgress = progress.pots;
        }
      })
      .addCase(syncGameState.rejected, () => {
        // Silent fail — pot/balance reset will happen on next spin/init
      })

      // Handle syncBalance - lightweight balance-only sync
      .addCase(syncBalance.fulfilled, (state, action) => {
        const { balance } = action.payload;
        if (balance !== undefined) {
          state.balance = Math.max(0, balance);
          state.displayedBalance = Math.max(0, balance);
        }
      })
      .addCase(syncBalance.rejected, () => {
        // Silent fail - balance sync is not critical
      })

      // Handle refreshGameData - lightweight init for error 205 (free rounds)
      // Updates game data WITHOUT resetting spinSlice state or reloading assets
      .addCase(refreshGameData.fulfilled, (state, action) => {
        const { result } = action.payload;

        // Simple state updates - RGS has handled feature detection
        state.balance = Math.max(0, result.balance);
        state.displayedBalance = Math.max(0, result.balance);
        state.bet = result.bet;
        // Override bet with locked FR bet when free rounds are active
        if (result.freeRounds?.bet_value) {
          state.bet = result.freeRounds.bet_value;
        }
        state.bets = result.bets;

        // Update currency config if provided
        if (result.currencyConfig) {
          state.currencyConfig = {
            subunits:
              result.currencyConfig.subunits !== undefined
                ? result.currencyConfig.subunits
                : 100,
            exponent:
              result.currencyConfig.exponent !== undefined
                ? result.currencyConfig.exponent
                : 2,
            symbol: result.currencyConfig.symbol || "",
          };
        }

        // Update current round if provided (intro recovery: trigger_screen + coin wins)
        if (result.round) {
          state.currentRound = result.round;
        }
        // Paylines layout — needed for win controller matchHorizontalWinningLines
        if (result.winningLines) {
          state.winningLines = result.winningLines;
        }

        // CRITICAL: Update featureState/gameMode so first free round spin sends correct request
        state.gameMode = result.gameMode || "base";
        state.featureState = result.featureState || {
          type: null,
          phase: null,
          data: null,
        };
        state.reloadState = result.reloadState || { needsReload: false };

        // Provider-specific fields
        state.availableActions = result.availableActions || ["init", "spin"];
        state.flowState = result.flowState || "ready";
        state.freeRounds = result.freeRounds || null;

        // Update jackpot multipliers and recompute display values
        state.jackpotMultipliers = result.jackpotMultipliers || state.jackpotMultipliers;
        state.jackpotValues = computeJackpotValues(state.bet, state.jackpotMultipliers);

        // Clear error state
        state.error = null;
        state.errorCode = null;
        state.errorDescription = null;
        state.status = "idle";
      })
      .addCase(refreshGameData.rejected, (state, action) => {
        state.error = action.payload || "Failed to refresh game data";
        state.status = "failed";
      });
  },
});

// ==============================
// 🔹 Selectors
// ==============================

export const selectToken = (state) => state.game.token;
export const selectBalance = (state) => state.game.balance;
export const selectDisplayedBalance = (state) => state.game.displayedBalance;
export const selectBet = (state) => state.game.bet;
export const selectBaseBet = (state) => state.game.baseBet || 1;
export const selectBets = (state) => state.game.bets;
export const selectCurrency = (state) => state.game.currency;
export const selectCurrencyConfig = (state) => state.game.currencyConfig;
export const selectPaytable = (state) => state.game.paytable;
export const selectRtp = (state) => state.game.rtp;
export const selectBonusBuyMultiplier = (state) =>
  state.game.bonusBuyMultiplier;
export const selectBonusBuyExtraMultiplier = (state) =>
  state.game.bonusBuyExtraMultiplier;
export const selectCurrentRound = (state) => state.game.currentRound;
export const selectWinningLines = (state) => state.game.winningLines;
export const selectScatterCount = (state) =>
  state.game.currentRound?.scatterCount || 0;
export const selectScatterPositions = (state) =>
  state.game.currentRound?.scatterPositions || [];
export const selectWinType = (state) => state.game.currentRound?.winType || "";
export const selectGameStatus = (state) => state.game.status;
export const selectGameError = (state) => state.game.error;
export const selectErrorCode = (state) => state.game.errorCode;
export const selectErrorDescription = (state) => state.game.errorDescription;

// Universal state selectors (provider-agnostic)
export const selectGameMode = (state) => state.game.gameMode;
export const selectFeatureState = (state) => state.game.featureState;
export const selectReloadState = (state) => state.game.reloadState;

// Legacy provider-specific selectors (will be removed in Phase 12)
export const selectAvailableActions = (state) => state.game.availableActions;
export const selectFlowState = (state) => state.game.flowState;
export const selectFeatures = (state) => state.game.features;
export const selectLastCommand = (state) => state.game.lastCommand;
export const selectHeartbeatError = (state) => state.game.heartbeatError;
export const selectFreeRounds = (state) => state.game.freeRounds;
export const selectJackpotValues = (state) => state.game.jackpotValues;
export const selectJackpotMultipliers = (state) => state.game.jackpotMultipliers;
export const selectPotProgress = (state) => state.game.potProgress;

// ==============================
// 🔹 Export Actions
// ==============================

export const {
  incrementBet,
  decrementBet,
  setBalance,
  setBet,
  setBaseBet,
  setDisplayedBalance,
  updateDisplayedBalance,
  setAvailableActions,
  setFlowState,
  setLastCommand,
  restoreSavedBet,
  clearFeatureStateToBase,
  keepLostRoundMoney,
  setFeatureStateFromFreeRounds,
  updateReelSymbols,
} = gameSettingsSlice.actions;

// Export the reducer to integrate into the Redux store
export default gameSettingsSlice.reducer;
